/**
 * Module 'audit-ops': the audit explorer and export, retention with a hash-chain-preserving
 * archive, full-chain verification, local detection rules, and the hub folder's backup and
 * format versions (architecture doc 8.2 Audit, 9 phase 4, 11.9, 11.10).
 *
 *   GET  /api/audit/query?...            AuditQueryResult    filter by actor, object, action, machine, result, time
 *   GET  /api/audit/export?format=csv|jsonl&...              the same filter as a download (admin)
 *   GET  /api/audit/verify               ChainVerifyResult   archive segments plus the live log
 *   GET  /api/audit/retention            RetentionState
 *   PUT  /api/audit/retention            RetentionSettings   (admin)
 *   POST /api/audit/retention/run        RetentionRunResult  archive and prune now (admin)
 *   GET  /api/security                   SecurityState       alerts, rules, folder format, last backup
 *   POST /api/security/alerts/:id/ack    {note?}             (operator)
 *   PUT  /api/security/rules             DetectionSettings   (admin)
 *
 * Browser-safe: the filter and the export formats live here so the hub and the demo UI agree.
 */
import type { AuditEntry, Id, Ts } from '../index.ts';
import type { ChainedAuditEntry } from './trust.ts';

export const AUDIT_OPS_EXT = 'audit-ops';

// ---------------------------------------------------------------------------
// Query and export
// ---------------------------------------------------------------------------

export interface AuditQuery {
  /** Any of these actors (exact). */
  actor?: string[];
  /** Any of these action names (exact): core ActionKinds and module names such as trust.freeze. */
  action?: string[];
  /** Any of these machines (exact id; 'hub' for hub-wide entries). */
  machine?: string[];
  /** The object acted on: a case-insensitive substring of the entry's target. */
  object?: string;
  result?: 'ok' | 'error';
  /** Free text over target, message, action and actor. */
  q?: string;
  /** Inclusive lower bound on `at`. */
  from?: Ts;
  /** Exclusive upper bound on `at`. */
  to?: Ts;
  /** Paging: strictly older than this. */
  before?: Ts;
  /**
   * Paging tie-break: with `before`, also entries at exactly `before` whose id sorts below this.
   * Pages are ordered newest first, ties by id descending, so (before, beforeId) of the last row
   * is a cursor that neither skips nor repeats entries written in the same millisecond.
   */
  beforeId?: string;
  limit?: number;
  /** Also search archived segments (slower; the live log is always searched). */
  archive?: boolean;
}

export interface AuditFacet {
  value: string;
  count: number;
}

export interface AuditQueryResult {
  /** Newest first. */
  entries: AuditEntry[];
  more: boolean;
  /** Entries matching the filter (all pages). */
  total: number;
  /** Counts over the entries matching every other filter, for the pickers. */
  facets: { actor: AuditFacet[]; action: AuditFacet[]; machine: AuditFacet[] };
  /** Entries searched (live, plus archive when asked). */
  scanned: number;
  archive: { searched: boolean; entries: number; unreadable: string[] };
}

export type AuditExportFormat = 'csv' | 'jsonl';

const lower = (s: unknown): string => (typeof s === 'string' ? s.toLowerCase() : '');

/** The order of every audit page: newest first, ties by id descending. */
export function compareAuditNewestFirst(a: AuditEntry, b: AuditEntry): number {
  return b.at - a.at || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);
}

/** True when the entry comes after the paging cursor (before, beforeId) in page order. */
export function pastAuditCursor(e: AuditEntry, q: Pick<AuditQuery, 'before' | 'beforeId'>): boolean {
  if (q.before == null) return true;
  if (e.at < q.before) return true;
  return e.at === q.before && q.beforeId != null && e.id < q.beforeId;
}

/** True when the entry passes every filter in the query except the ones named in `skip`. */
export function matchAudit(e: AuditEntry, q: AuditQuery, skip: ReadonlySet<'actor' | 'action' | 'machine'> = new Set()): boolean {
  if (q.from != null && e.at < q.from) return false;
  if (q.to != null && e.at >= q.to) return false;
  if (!pastAuditCursor(e, q)) return false;
  if (q.result && e.result !== q.result) return false;
  if (!skip.has('actor') && q.actor?.length && !q.actor.includes(e.actor)) return false;
  if (!skip.has('action') && q.action?.length && !q.action.includes(e.action)) return false;
  if (!skip.has('machine') && q.machine?.length && !q.machine.includes(e.machineId)) return false;
  if (q.object && !lower(e.target).includes(q.object.toLowerCase())) return false;
  if (q.q) {
    const hay = `${lower(e.target)} ${lower(e.message)} ${lower(e.action)} ${lower(e.actor)}`;
    if (!hay.includes(q.q.toLowerCase())) return false;
  }
  return true;
}

/** Facet counts for the pickers: each facet ignores its own filter so it can widen as well as narrow. */
export function auditFacets(entries: readonly AuditEntry[], q: AuditQuery): AuditQueryResult['facets'] {
  const count = (field: 'actor' | 'action' | 'machine', key: (e: AuditEntry) => string): AuditFacet[] => {
    const m = new Map<string, number>();
    const skip = new Set([field]);
    for (const e of entries) if (matchAudit(e, { ...q, before: undefined, beforeId: undefined }, skip)) m.set(key(e), (m.get(key(e)) ?? 0) + 1);
    return [...m.entries()].map(([value, n]) => ({ value, count: n })).sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
  };
  return { actor: count('actor', (e) => e.actor), action: count('action', (e) => e.action), machine: count('machine', (e) => e.machineId) };
}

/** Run a query over entries in any order. The demo UI uses this; the hub uses it over its index. */
export function runAuditQuery(all: readonly AuditEntry[], q: AuditQuery): Omit<AuditQueryResult, 'scanned' | 'archive'> {
  const limit = Math.max(1, Math.min(q.limit ?? 100, 1000));
  const sorted = [...all].sort(compareAuditNewestFirst);
  const matching = sorted.filter((e) => matchAudit(e, { ...q, before: undefined, beforeId: undefined }));
  const page = matching.filter((e) => pastAuditCursor(e, q));
  return { entries: page.slice(0, limit), more: page.length > limit, total: matching.length, facets: auditFacets(sorted, q) };
}

/** What parseAuditQuery reads: URLSearchParams in the browser and in Node. */
export interface QueryParamsLike {
  get(name: string): string | null;
  getAll(name: string): string[];
}

/** URL query string for an AuditQuery (repeatable keys for lists). */
export function auditQueryString(q: AuditQuery): string {
  const parts: string[] = [];
  const add = (k: string, v: string) => parts.push(`${k}=${encodeURIComponent(v)}`);
  for (const a of q.actor ?? []) add('actor', a);
  for (const a of q.action ?? []) add('action', a);
  for (const m of q.machine ?? []) add('machine', m);
  if (q.object) add('object', q.object);
  if (q.result) add('result', q.result);
  if (q.q) add('q', q.q);
  if (q.from != null) add('from', String(q.from));
  if (q.to != null) add('to', String(q.to));
  if (q.before != null) add('before', String(q.before));
  if (q.before != null && q.beforeId) add('beforeId', q.beforeId);
  if (q.limit != null) add('limit', String(q.limit));
  if (q.archive) add('archive', '1');
  return parts.join('&');
}

/** The inverse of auditQueryString; unknown or malformed values are dropped. */
export function parseAuditQuery(p: QueryParamsLike): AuditQuery {
  const num = (k: string): number | undefined => {
    const v = p.get(k);
    const n = v == null || v === '' ? NaN : Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const list = (k: string): string[] | undefined => {
    const v = p.getAll(k).filter((x: string) => x).slice(0, 50);
    return v.length ? v : undefined;
  };
  const result = p.get('result');
  const q: AuditQuery = {};
  const actor = list('actor');
  const action = list('action');
  const machine = list('machine');
  if (actor) q.actor = actor;
  if (action) q.action = action;
  if (machine) q.machine = machine;
  if (p.get('object')) q.object = p.get('object')!.slice(0, 200);
  if (result === 'ok' || result === 'error') q.result = result;
  if (p.get('q')) q.q = p.get('q')!.slice(0, 200);
  const from = num('from');
  const to = num('to');
  const before = num('before');
  const limit = num('limit');
  if (from != null) q.from = from;
  if (to != null) q.to = to;
  if (before != null) q.before = before;
  const beforeId = p.get('beforeId');
  if (before != null && beforeId) q.beforeId = beforeId.slice(0, 100);
  if (limit != null) q.limit = limit;
  if (p.get('archive') === '1') q.archive = true;
  return q;
}

/** CSV columns, in order. `params` is JSON; hashes are present on chained entries. */
export const AUDIT_CSV_COLUMNS = ['time', 'id', 'actor', 'action', 'object', 'machine_id', 'machine', 'result', 'message', 'params', 'prev_hash', 'hash'] as const;

/** One CSV cell: quoted when needed, and a leading = + - @ is neutralised so spreadsheets never run it. */
export function csvCell(v: unknown): string {
  let s = v == null ? '' : typeof v === 'string' ? v : JSON.stringify(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function auditCsvHeader(): string {
  return `${AUDIT_CSV_COLUMNS.join(',')}\r\n`;
}

export function auditCsvRow(e: AuditEntry, machineName = ''): string {
  const x = e as AuditEntry & { prevHash?: string; hash?: string };
  const actor = typeof e.params?.onBehalfOf === 'string' && ['orgus', 'policy', 'system'].includes(e.actor) ? `Orgus, on behalf of ${e.params.onBehalfOf}` : e.actor;
  return `${[new Date(e.at).toISOString(), e.id, actor, e.action, e.target, e.machineId, machineName, e.result, e.message ?? '', e.params ?? {}, x.prevHash ?? '', x.hash ?? '']
    .map(csvCell)
    .join(',')}\r\n`;
}

/**
 * One JSON Lines record for a SIEM: Elastic Common Schema field names (`@timestamp`, `event`,
 * `user`, `host`) for search, plus the entry exactly as written under `orgus`, so the hash
 * chain can be checked downstream as well.
 */
export interface AuditSiemRecord {
  '@timestamp': string;
  event: { id: string; kind: 'event'; dataset: 'orgus.audit'; action: string; outcome: 'success' | 'failure'; reason?: string };
  user: { name: string };
  host: { id: string; name?: string };
  orgus: AuditEntry;
}

export function auditSiemRecord(e: AuditEntry, machineName?: string): AuditSiemRecord {
  return {
    '@timestamp': new Date(e.at).toISOString(),
    event: { id: e.id, kind: 'event', dataset: 'orgus.audit', action: e.action, outcome: e.result === 'ok' ? 'success' : 'failure', ...(e.message ? { reason: e.message } : {}) },
    user: { name: e.actor },
    host: { id: e.machineId, ...(machineName ? { name: machineName } : {}) },
    orgus: e,
  };
}

// ---------------------------------------------------------------------------
// Audit streaming sinks (ORGU-399)
// ---------------------------------------------------------------------------

export type AuditSinkKind = 'datadog' | 'json';

export interface AuditSink {
  id: string;
  kind: AuditSinkKind;
  url: string;
  apiKey?: string | null;
  name?: string;
  enabled?: boolean;
  createdAt?: number;
  updatedAt?: number;
}

/** WHATWG URL, the parser fetch and Node use (this package has no DOM or Node types). */
declare const URL: new (input: string) => { protocol: string; toString(): string };

/**
 * Validates an audit sink URL. Plain http:// URLs are strictly rejected (AC-3).
 */
export function validateSinkUrl(rawUrl: string): { ok: true; url: string } | { ok: false; error: string } {
  if (!rawUrl || typeof rawUrl !== 'string') {
    return { ok: false, error: 'Sink URL is required.' };
  }
  const trimmed = rawUrl.trim();
  if (trimmed.startsWith('http://')) {
    return { ok: false, error: 'Plain http:// URLs are rejected; audit sinks must use https://.' };
  }
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === 'http:') {
      return { ok: false, error: 'Plain http:// URLs are rejected; audit sinks must use https://.' };
    }
    if (parsed.protocol !== 'https:') {
      return { ok: false, error: `Audit sinks must use https:// (got ${parsed.protocol}).` };
    }
    return { ok: true, url: parsed.toString() };
  } catch {
    return { ok: false, error: 'Sink URL is not a valid URL.' };
  }
}

/**
 * Format an audit entry for Datadog Logs intake (/api/v2/logs) with the chain hash attached (AC-1).
 */
export interface AuditDatadogRecord {
  ddsource: string;
  service: string;
  hostname: string;
  message: string;
  timestamp: number;
  date: string;
  status: 'info' | 'error';
  ddtags: string;
  hash: string;
  chainHash: string;
  chain_hash: string;
  seq: number;
  sequenceNumber: number;
  sequence_number: number;
  event: AuditEntry;
  orgus: AuditEntry;
  [key: string]: unknown;
}

export function auditDatadogRecord(e: ChainedAuditEntry, machineName?: string): AuditDatadogRecord {
  const hash = e.hash ?? '';
  const seq = e.seq ?? 0;
  const status = e.result === 'ok' ? 'info' : 'error';
  const hostname = machineName ?? e.machineId;
  const message = e.message || `${e.actor} ${e.action} ${e.target} (${e.result})`;
  return {
    ddsource: 'orgus',
    service: 'orgus',
    hostname,
    message,
    timestamp: e.at,
    date: new Date(e.at).toISOString(),
    status,
    ddtags: `actor:${e.actor},action:${e.action},result:${e.result}`,
    hash,
    chainHash: hash,
    chain_hash: hash,
    seq,
    sequenceNumber: seq,
    sequence_number: seq,
    event: e,
    orgus: e,
    ...e,
  };
}

/**
 * Format an audit entry for a generic JSON HTTPS endpoint with the event, chain hash and sequence number (AC-2).
 */
export interface AuditGenericJsonRecord {
  event: ChainedAuditEntry;
  hash: string;
  chainHash: string;
  chain_hash: string;
  seq: number;
  sequenceNumber: number;
  sequence_number: number;
  [key: string]: unknown;
}

export function auditGenericJsonRecord(e: ChainedAuditEntry, _machineName?: string): AuditGenericJsonRecord {
  const hash = e.hash ?? '';
  const seq = e.seq ?? 0;
  return {
    event: e,
    hash,
    chainHash: hash,
    chain_hash: hash,
    seq,
    sequenceNumber: seq,
    sequence_number: seq,
    ...e,
  };
}

// ---------------------------------------------------------------------------
// Retention and the archive
// ---------------------------------------------------------------------------

export interface RetentionSettings {
  /** Entries older than this move from audit.jsonl to the archive. null: never. */
  liveDays: number | null;
  /** Archived segments older than this are deleted (their hashes stay in the manifest). null: never. */
  archiveDays: number | null;
}

export const RETENTION_DEFAULTS: RetentionSettings = { liveDays: 90, archiveDays: null };
export const RETENTION_LIMITS = { minLiveDays: 1, maxDays: 3650 } as const;

/** One archived run of audit entries: a gzip of the exact lines, so every hash still verifies. */
export interface ArchiveSegment {
  /** File name inside <hub>/audit-archive/. */
  file: string;
  /** Oldest and newest entry times. */
  from: Ts;
  to: Ts;
  count: number;
  firstId: string | null;
  lastId: string | null;
  /** What the first chained entry in the segment commits to (the end of the previous segment). */
  startHash: string;
  /** What the entry after this segment commits to. */
  endHash: string;
  /** sha256 of the segment file. */
  sha256: string;
  bytes: number;
  archivedAt: Ts;
  by: string;
  /** Set once the segment file is deleted by archive retention. Its hashes stay so the chain still verifies. */
  pruned?: { at: Ts; by: string };
}

export interface RetentionRun {
  at: Ts;
  by: string;
  archived: number;
  pruned: number;
  ok: boolean;
  message: string;
}

/** GET /api/audit/retention */
export interface RetentionState {
  settings: RetentionSettings;
  live: { entries: number; oldest: Ts | null; bytes: number };
  archive: {
    segments: ArchiveSegment[];
    /** Entries in segments still on disk. */
    entries: number;
    /** Entries whose segments were pruned. */
    pruned: number;
    oldest: Ts | null;
  };
  lastRun: RetentionRun | null;
  nextRunAt: Ts | null;
}

/** POST /api/audit/retention/run */
export type RetentionRunResult = RetentionRun;

/** GET /api/audit/verify: the archive's segments in order, then the live log. */
export interface ChainVerifyResult {
  ok: boolean;
  checkedAt: Ts;
  /** Entries whose links were checked (archive on disk plus live). */
  verified: number;
  live: { entries: number; chained: number; legacy: number; head: string | null; anchorMatches: boolean | null };
  archive: { segments: number; entries: number; prunedSegments: number; prunedEntries: number };
  brokenAt?: { where: 'archive' | 'live'; file: string; line: number; id: string | null; reason: string };
  /** One sentence for people. */
  summary: string;
}

// ---------------------------------------------------------------------------
// Detection
// ---------------------------------------------------------------------------

export type SecurityRuleId = 'new-address' | 'credential-two-places' | 'command-burst' | 'repeated-denials';
export const SECURITY_RULE_IDS: readonly SecurityRuleId[] = ['new-address', 'credential-two-places', 'command-burst', 'repeated-denials'];

export interface DetectionRule {
  enabled: boolean;
  /** How many events within the window raise the alert (not used by new-address). */
  threshold: number;
  windowSec: number;
}

export type DetectionSettings = Record<SecurityRuleId, DetectionRule>;

export const DETECTION_DEFAULTS: DetectionSettings = {
  'new-address': { enabled: true, threshold: 1, windowSec: 0 },
  'credential-two-places': { enabled: true, threshold: 2, windowSec: 300 },
  'command-burst': { enabled: true, threshold: 30, windowSec: 60 },
  'repeated-denials': { enabled: true, threshold: 5, windowSec: 600 },
};

export const SECURITY_RULE_LABEL: Record<SecurityRuleId, { title: string; description: string }> = {
  'new-address': {
    title: 'Machine on a new network',
    description: 'A machine connects from a network it has never used before (a new /24 for IPv4, /64 for IPv6).',
  },
  'credential-two-places': {
    title: 'Machine credentials used from two places',
    description: 'The same machine identity connects from two addresses that keep replacing each other within the window (a machine moving to a new network does not count), or a copied secret is presented without its key.',
  },
  'command-burst': {
    title: 'Burst of commands',
    description: 'One person or agent sends more actions than the threshold within the window.',
  },
  'repeated-denials': {
    title: 'Repeated refusals',
    description: 'One person or agent has more actions refused by policy, a kill switch or a machine ceiling than the threshold within the window.',
  },
};

export type AlertSeverity = 'warning' | 'critical';

export interface SecurityAlert {
  id: Id;
  rule: SecurityRuleId;
  severity: AlertSeverity;
  title: string;
  detail: string;
  /** What to do about it. */
  next: string;
  /** First and latest occurrence. */
  at: Ts;
  lastAt: Ts;
  /** Occurrences folded into this alert. */
  count: number;
  machineId: Id | null;
  actor: string | null;
  status: 'open' | 'acknowledged';
  ack?: { by: string; at: Ts; note: string };
  evidence: Record<string, unknown>;
}

/** Where the hub folder's format stands (<hub>/format.json). */
export interface FolderFormatInfo {
  versions: Record<string, number>;
  /** The newest version this hub knows per scope. */
  known: Record<string, number>;
  history: { id: string; scope: string; to: number; at: Ts; description: string }[];
}

export interface BackupRecord {
  at: Ts;
  /** live: the running hub took the snapshot; offline: the CLI read a stopped hub's folder. */
  mode: 'live' | 'offline';
  files: number;
  bytes: number;
  /** Where the CLI wrote the archive, when it reported it. */
  file?: string;
  by: string;
}

/** The last `orgus restore` into this hub folder (also a chained hub.restore audit entry). */
export interface RestoreRecord {
  at: Ts;
  /** When and where the restored backup was taken. */
  backupCreatedAt: Ts;
  backupHost: string;
  /** The audit log of the folder that was replaced: its head and entry count, so what was lost is on record. */
  replaced: { head: string | null; entries: number; intact: boolean } | null;
  /** Restored with --force over a chain that did not verify. */
  force: boolean;
  chainOk: boolean;
  /** Where the replaced folder was kept. */
  previous: string | null;
  by: string;
}

/** GET /api/security */
export interface SecurityState {
  /** Open first, then acknowledged; newest first within each. */
  alerts: SecurityAlert[];
  rules: DetectionSettings;
  format: FolderFormatInfo;
  lastBackup: BackupRecord | null;
  /** Absent from hubs older than restore auditing. */
  lastRestore?: RestoreRecord | null;
}

/** state.ext['audit-ops'] */
export interface AuditOpsSlice {
  openAlerts: number;
  critical: number;
  newestAlertAt: Ts | null;
  archivedEntries: number;
}

/** Labels for the audit entries this module and its core hooks write. */
export const AUDIT_OPS_AUDIT_LABEL: Record<string, string> = {
  'audit.archive': 'Archive audit entries',
  'audit.prune': 'Prune audit archive',
  'audit.retention': 'Change audit retention',
  'audit.export': 'Export audit log',
  'audit.sink': 'Configure audit streaming sink',
  'security.alert-ack': 'Acknowledge security alert',
  'security.rules': 'Change detection rules',
  'machine.revoke': 'Revoke machine',
  'hub.backup': 'Back up hub',
  'hub.restore': 'Restore hub from backup',
};
